import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { roleHasPermission } from "@shared/permissions";
import type { SecurityDeliveryRow } from "@shared/types/notifications";
import type { PutSecurityPrefsBody, SecurityPrefsView } from "@shared/types/securityAlerts";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";

/**
 * Security alerts (docs/research/2026-09-28-design-security-alerts.md): the caller's own
 * subscription (TenantAdmin and PlatformAdmin) and the platform's security deliveries
 * (PlatformAdmin). Neither route is instance-scoped, so no key carries an instance.
 */

export const SECURITY_PREFS_PATH = "/api/v2/me/notifications/security";
export const SECURITY_DELIVERIES_PATH = "/api/v2/platform/notifications/security/deliveries";
export const SECURITY_PREFS_KEY = [SECURITY_PREFS_PATH] as const;
export const SECURITY_DELIVERIES_KEY = [SECURITY_DELIVERIES_PATH] as const;

/**
 * Whether the caller may subscribe: the route's own rule (`tenant.users.manage`, checked on the
 * highest role held at platform or tenant scope), read from the assignments `/me` returned and
 * the shared role catalogue, never from a role name typed here.
 */
export function useCanSubscribeToSecurityAlerts(): boolean {
  const { me } = useTenantInstance();
  return useMemo(
    () =>
      (me?.assignments ?? []).some(
        (assignment) =>
          (assignment.scopeType === "platform" || assignment.scopeType === "tenant") &&
          roleHasPermission(assignment.roleName, "tenant.users.manage"),
      ),
    [me],
  );
}

export function useSecurityPrefs(enabled = true) {
  return useQuery<SecurityPrefsView>({ queryKey: SECURITY_PREFS_KEY, enabled });
}

export function useSaveSecurityPrefs() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutSecurityPrefsBody) =>
      apiRequest<SecurityPrefsView>(SECURITY_PREFS_PATH, { method: "PUT", body }),
    onSuccess: (view) => {
      client.setQueryData(SECURITY_PREFS_KEY, view);
    },
  });
}

export function useSecurityDeliveries(enabled = true) {
  return useQuery<SecurityDeliveryRow[]>({ queryKey: SECURITY_DELIVERIES_KEY, enabled });
}
